import { useState } from "react";
import { ArrowLeft, ArrowRight, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, waLink } from "@/i18n";
import { api } from "@/api";

const LOVES = [
  ["history", "History", "Storia"], ["archaeology", "Archaeology", "Archeologia"], ["sea", "Sea", "Mare"],
  ["boat", "Boat", "Barca"], ["canoe", "Canoe", "Canoa"], ["islands", "Islands", "Isole"],
  ["nature", "Nature", "Natura"], ["trekking", "Trekking", "Trekking"], ["mountains", "Mountains", "Montagne"],
  ["food", "Food", "Cibo"], ["wine", "Wine", "Vino"], ["art", "Art", "Arte"],
  ["nightlife", "Nightlife", "Vita notturna"], ["relax", "Relax", "Relax"], ["photography", "Photography", "Fotografia"],
];

const WHERES = [
  ["campi-flegrei", "Campi Flegrei"], ["napoli", "Napoli"], ["procida", "Procida"], ["ischia", "Ischia"],
  ["capri", "Capri"], ["pompei", "Pompei"], ["vesuvio", "Vesuvio"], ["costiera", "Costiera"],
  ["caserta", "Caserta"], ["appennino", "Appennino"], ["all", "Tutta la Campania"],
];

const EXTRAS = [
  ["aperitivo", "Aperitivo"], ["pranzo", { it: "Pranzo", en: "Lunch" }], ["cena", { it: "Cena", en: "Dinner" }],
  ["degustazione", { it: "Degustazione", en: "Tasting" }], ["transfer", "Transfer"], ["bb", "B&B"],
  ["boat", "Boat"], ["canoe", "Canoa"], ["bike", "Bike"], ["car", { it: "Auto", en: "Car" }],
];

const STEPS = ["step1", "step2", "step3", "step4", "step5", "step6", "step7", "step8"];

export default function CreateExperience() {
  const { lang, t } = useLanguage();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    travel_with: "", people: "", date: "", flexible: false,
    loves: [], where: [], extras: [], notes: "",
    name: "", email: "", phone: "", whatsapp: "",
  });

  const toggle = (key, value) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));
  };

  const canNext = () => {
    if (step === 0) return !!form.travel_with;
    if (step === 1) return !!form.people;
    if (step === 2) return !!form.date || form.flexible;
    if (step === 3) return form.loves.length > 0;
    if (step === 4) return form.where.length > 0;
    if (step === 7) return !!form.name && !!form.email;
    return true;
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.createInquiry({
        travel_with: form.travel_with,
        people: form.people,
        date: form.flexible ? "flexible" : form.date,
        loves: form.loves,
        where: form.where,
        extras: form.extras,
        notes: form.notes,
        name: form.name,
        email: form.email,
        phone: form.phone,
        whatsapp: form.whatsapp,
        language: lang,
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error(lang === "it" ? "Errore nell'invio. Riprova o scrivici su WhatsApp." : "Submission error. Please retry or message us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const waSummary = () => {
    const lines = [
      t("wa_message"),
      `${t("step1")} ${form.travel_with}`,
      `${t("step2")} ${form.people}`,
      `${t("step3")} ${form.flexible ? "flexible" : form.date}`,
      `${t("step4")} ${form.loves.join(", ")}`,
      `${t("step5")} ${form.where.join(", ")}`,
      form.extras.length ? `${t("step6")} ${form.extras.join(", ")}` : null,
      form.notes ? `${t("step7")} ${form.notes}` : null,
      `${t("name_label")}: ${form.name} — ${form.email}`,
    ].filter(Boolean);
    return waLink(lines.join("\n"));
  };

  if (submitted) {
    return (
      <div className="py-32 lg:py-44" data-testid="create-thankyou">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-8">
            <Check size={28} strokeWidth={1.5} className="text-sage" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight" data-testid="thankyou-title">{t("thankyou")}</h1>
          <p className="mt-6 text-ink/60 leading-relaxed">{t("thankyou_sub")}</p>
          <a
            href={waSummary()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thankyou-whatsapp"
            className="mt-10 inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink transition-colors duration-300"
          >
            <MessageCircle size={15} strokeWidth={1.5} /> {t("thankyou_wa")}
          </a>
        </div>
      </div>
    );
  }

  const chip = (active) =>
    `text-sm px-5 py-3 border transition-colors duration-300 ${
      active ? "bg-ink text-ivory border-ink" : "border-stone2 text-ink/70 hover:border-ink"
    }`;

  return (
    <div className="py-20 lg:py-28" data-testid="create-page">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-medblue mb-4">Bespoke</p>
        <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight" data-testid="create-title">{t("create_title")}</h1>
        <p className="mt-4 text-ink/60 text-base md:text-lg">{t("create_sub")}</p>
        <a href="/day-builder" data-testid="create-day-builder-link" className="inline-flex items-center gap-2 mt-6 text-[11px] uppercase tracking-[0.2em] text-medblue hover:text-ink transition-colors duration-300">
          {t("day_title")} →
        </a>

        {/* Progress */}
        <div className="mt-12 mb-10" data-testid="wizard-progress">
          <div className="flex justify-between text-[11px] uppercase tracking-[0.2em] text-ink/40 mb-3">
            <span>Step {step + 1} {t("step_of")} 8</span>
            <span>{t(STEPS[step])}</span>
          </div>
          <div className="h-px bg-stone2 relative">
            <div className="absolute left-0 top-0 h-px bg-medblue transition-all duration-500" style={{ width: `${((step + 1) / 8) * 100}%` }} />
          </div>
        </div>

        <div className="min-h-[320px]" data-testid={`wizard-step-${step + 1}`}>
          {step === 0 && (
            <div className="flex flex-wrap gap-3">
              {["opt_solo", "opt_couple", "opt_family", "opt_friends", "opt_group"].map((o) => (
                <button key={o} data-testid={`travel-with-${o.replace("opt_", "")}`} onClick={() => setForm({ ...form, travel_with: t(o) })} className={chip(form.travel_with === t(o))}>
                  {t(o)}
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="flex flex-wrap gap-3">
              {[...Array(10)].map((_, i) => (
                <button key={i} data-testid={`people-${i + 1}`} onClick={() => setForm({ ...form, people: String(i + 1) })} className={chip(form.people === String(i + 1))}>
                  {i + 1}
                </button>
              ))}
              <button data-testid="people-more" onClick={() => setForm({ ...form, people: "10+" })} className={chip(form.people === "10+")}>
                {t("more_than_10")}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <input
                type="date"
                data-testid="date-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value, flexible: false })}
                className="border border-stone2 bg-ivory px-5 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
              />
              <label className="flex items-center gap-3 text-sm text-ink/70 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="flexible-dates-checkbox"
                  checked={form.flexible}
                  onChange={(e) => setForm({ ...form, flexible: e.target.checked })}
                  className="w-4 h-4 accent-[#1D4E89]"
                />
                {t("date_flexible")}
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-wrap gap-3">
              {LOVES.map(([value, en, it]) => (
                <button key={value} data-testid={`love-${value}`} onClick={() => toggle("loves", lang === "it" ? it : en)} className={chip(form.loves.includes(lang === "it" ? it : en))}>
                  {lang === "it" ? it : en}
                </button>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="flex flex-wrap gap-3">
              {WHERES.map(([value, label]) => (
                <button key={value} data-testid={`where-${value}`} onClick={() => toggle("where", label)} className={chip(form.where.includes(label))}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="flex flex-wrap gap-3">
              {EXTRAS.map(([value, label]) => {
                const text = typeof label === "string" ? label : label[lang];
                return (
                  <button key={value} data-testid={`extra-${value}`} onClick={() => toggle("extras", text)} className={chip(form.extras.includes(text))}>
                    {text}
                  </button>
                );
              })}
            </div>
          )}

          {step === 6 && (
            <textarea
              data-testid="notes-textarea"
              rows={6}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t("notes_placeholder")}
              className="w-full border border-stone2 bg-ivory px-5 py-4 text-sm text-ink focus:outline-none focus:border-medblue resize-none"
            />
          )}

          {step === 7 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" placeholder={t("name_label")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue" />
              <input data-testid="contact-email" type="email" placeholder={t("email_label")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue" />
              <input data-testid="contact-phone" placeholder={t("phone_label")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue" />
              <input data-testid="contact-whatsapp" placeholder={t("whatsapp_label")} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                className="border border-stone2 bg-ivory px-5 py-3.5 text-sm text-ink focus:outline-none focus:border-medblue" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-12 pt-8 border-t border-stone2/50">
          <button
            data-testid="wizard-back"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/50 hover:text-ink disabled:opacity-30 transition-colors duration-300"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> {t("back")}
          </button>
          {step < 7 ? (
            <button
              data-testid="wizard-next"
              onClick={() => canNext() && setStep((s) => s + 1)}
              disabled={!canNext()}
              className="inline-flex items-center gap-2 bg-ink text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-medblue disabled:opacity-30 transition-colors duration-300"
            >
              {t("next")} <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          ) : (
            <button
              data-testid="wizard-submit"
              onClick={submit}
              disabled={!canNext() || sending}
              className="inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ink disabled:opacity-30 transition-colors duration-300"
            >
              {sending ? "..." : t("submit")} <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
